/**
 * Comparacao entre a janela atual e a anterior, projecao e participacao --
 * tudo em inteiros.
 *
 * Copia local do mesmo contrato de features/admin-central/comparar.ts:
 * modulos nascem em paralelo e nao importam um do outro (pedido de promocao
 * para lib/ no relatorio). Mesmas regras:
 *  1. A variacao relativa sai em BPS inteiros (1234 = +12,34%), calculada uma
 *     vez aqui. Nenhum componente faz `(a - b) / b` por conta propria.
 *  2. `sem_base` e um estado, nao um zero: anterior nulo ("desde o inicio")
 *     ou zero com atual positivo nao tem percentual honesto.
 *  3. Direcao e fato aritmetico; se e boa ou ruim, quem decide e o consumidor.
 */

export type Direcao = 'alta' | 'queda' | 'igual' | 'sem_base';

export interface Variacao {
  readonly direcao: Direcao;
  /** `atual - anterior`, na unidade da medida. Zero quando sem base. */
  readonly absoluta: number;
  /** Variacao relativa em bps; `null` quando nao ha base para dividir. */
  readonly relativaBps: number | null;
}

export const SEM_BASE: Variacao = { direcao: 'sem_base', absoluta: 0, relativaBps: null };

export function variar(atual: number, anterior: number | null): Variacao {
  if (anterior === null || !Number.isFinite(anterior) || !Number.isFinite(atual)) {
    return SEM_BASE;
  }
  const absoluta = atual - anterior;
  if (absoluta === 0) {
    return { direcao: 'igual', absoluta: 0, relativaBps: 0 };
  }
  if (anterior === 0) {
    return { direcao: absoluta > 0 ? 'alta' : 'queda', absoluta, relativaBps: null };
  }
  return {
    direcao: absoluta > 0 ? 'alta' : 'queda',
    absoluta,
    relativaBps: Math.round((absoluta * 10_000) / Math.abs(anterior)),
  };
}

/**
 * Projecao linear inteira: o que `realizado` em `decorridos` dias vira em
 * `total` dias no mesmo ritmo. Sem dia decorrido, nao ha ritmo: `null`.
 */
export function projetar(realizado: number, decorridos: number, total: number): number | null {
  if (decorridos <= 0 || total <= 0) {
    return null;
  }
  return Math.round((realizado * total) / decorridos);
}

/** Participacao de `parte` em `todo`, em bps; `null` quando `todo` e zero. */
export function participacaoBps(parte: number, todo: number): number | null {
  if (todo <= 0) {
    return null;
  }
  return Math.round((parte * 10_000) / todo);
}

/** Dias inteiros entre dois ISO (fim exclusivo), no minimo 1. */
export function diasEntre(inicioIso: string, fimIso: string): number {
  const inicio = new Date(inicioIso).getTime();
  const fim = new Date(fimIso).getTime();
  if (Number.isNaN(inicio) || Number.isNaN(fim) || fim <= inicio) {
    return 1;
  }
  return Math.max(1, Math.round((fim - inicio) / 86_400_000));
}

/** Dias inteiros de `desdeIso` ate `ateIso`, para "em implantacao ha N dias". */
export function diasDesde(desdeIso: string, ateIso: string): number {
  const desde = new Date(desdeIso).getTime();
  const ate = new Date(ateIso).getTime();
  if (Number.isNaN(desde) || Number.isNaN(ate) || ate <= desde) {
    return 0;
  }
  return Math.floor((ate - desde) / 86_400_000);
}

/** "3 dias", "1 dia", "hoje". */
export function descreverDias(dias: number): string {
  if (dias <= 0) {
    return 'hoje';
  }
  return dias === 1 ? '1 dia' : `${dias} dias`;
}
